/**
 * Local Email Testing Utility
 * 
 * This utility helps test email notifications locally without needing
 * to deploy the Supabase Edge Function.
 */

export interface LocalEmailTestData {
  taskId: string;
  title: string;
  description?: string;
  category: string;
  priority: 'high' | 'medium' | 'low';
  assignedToEmail: string;
  assignedToName: string;
  assignedByEmail: string;
  assignedByName: string;
}

/**
 * Generate a formatted email preview for local testing
 */
export const generateLocalEmailPreview = (taskData: LocalEmailTestData): string => {
  const emailSubject = `New Task Assigned: ${taskData.title}`;
  
  const emailBody = `
Hello ${taskData.assignedToName},

A new task has been assigned to you on the Climate Plans project:

Task Details:
- Title: ${taskData.title}
- Category: ${taskData.category}
- Priority: ${taskData.priority.toUpperCase()}
- Description: ${taskData.description || 'No description provided'}

Assigned by: ${taskData.assignedByName} (${taskData.assignedByEmail})
Assigned on: ${new Date().toLocaleDateString()}

You can view and manage this task in the Developers' Log at:
http://localhost:3000/developers-log

Best regards,
Climate Plans Team
  `.trim();

  return `
📧 EMAIL NOTIFICATION PREVIEW
═══════════════════════════════════════════════════════════════
To: ${taskData.assignedToEmail}
Subject: ${emailSubject}

${emailBody}
═══════════════════════════════════════════════════════════════
  `.trim();
};

/**
 * Test email notification locally
 */
export const testLocalEmailNotification = (taskData: LocalEmailTestData): void => {
  console.log(generateLocalEmailPreview(taskData));
  
  // Also show a browser alert for easy testing
  const preview = generateLocalEmailPreview(taskData);
  alert(preview);
};

/**
 * Create a test task data object
 */
export const createTestTaskData = (): LocalEmailTestData => {
  return {
    taskId: 'test-task-' + Date.now(),
    title: 'Test Task Assignment',
    description: 'This is a test task to verify email notifications are working correctly.',
    category: 'Testing',
    priority: 'high',
    assignedToEmail: 'test@example.com',
    assignedToName: 'Test Developer',
    assignedByEmail: 'admin@climateplans.org',
    assignedByName: 'Admin User'
  };
};
